/**
 * The words and figures of the Mailshots report (Campaigns beta), from the answer of getMailshotReport. Pure.
 *
 * ⚠️ A window that has not yet passed is NOT a count: it is "—" with the days to go, never 0 (Lee, 1 Nov 2026).
 * A window that has passed shows quotes per 1,000 homes mailed as the large figure, the count beneath.
 */
const n = (x) => Number(x || 0).toLocaleString('en-GB');
const plural = (k, one, many = `${one}s`) => `${n(k)} ${k === 1 ? one : many}`;

export const WINDOW_LABELS = Object.freeze({ w1: '1 week', m1: '1 month', m3: '3 months', m6: '6 months' });

/** One window's cell: { big, small, passed }. */
export function windowCell(w) {
  if (!w || w.passed !== true) {
    const days = w && Number.isFinite(w.daysToGo) ? w.daysToGo : null;
    return { passed: false, big: '—', small: days == null ? 'not yet' : `not yet: ${plural(days, 'day')} to go` };
  }
  return { passed: true, big: w.perThousand == null ? '—' : `${w.perThousand}`, small: plural(w.quotes, 'quote') };
}

/** "So far": every quote credited to the mailshot, per 1,000, the count beneath. */
export function soFarCell(row) {
  return { big: row.perThousand == null ? '—' : `${row.perThousand}`, small: plural(row.quotes, 'quote') };
}

/** Where its homes came from. */
export function sourceWords(row) {
  const parts = [];
  if (row.scrapes) parts.push(plural(row.scrapes, 'scrape'));
  if (row.imports) parts.push(plural(row.imports, 'import'));
  if (row.historical) parts.push('imported mailing');
  return parts.join(' · ') || '—';
}

/** The lines under the table: what is left out, and why, each said only when there are some. */
export function leftOutLines(totals) {
  const t = totals || {};
  const lines = [];
  if (t.goneLeftOut) lines.push(`${plural(t.goneLeftOut, 'quote')} HubSpot no longer has (deleted or merged away) ${t.goneLeftOut === 1 ? 'is' : 'are'} left out.`);
  if (t.testLeftOut) lines.push(`${plural(t.testLeftOut, 'quote')} at a test home ${t.testLeftOut === 1 ? 'is' : 'are'} left out, as on every page that counts conversions.`);
  if (t.beforeMailing) lines.push(`${plural(t.beforeMailing, 'quote')} dated before the mailshot ${t.beforeMailing === 1 ? 'it is' : 'they are'} credited to ${t.beforeMailing === 1 ? 'is' : 'are'} counted apart, in no window.`);
  if (t.undatedQuotes) lines.push(`${plural(t.undatedQuotes, 'quote')} with no date ${t.undatedQuotes === 1 ? 'is' : 'are'} counted apart, in no window.`);
  if (t.unattributed) lines.push(`${plural(t.unattributed, 'quote')} from a home with no sent, dated mailshot ${t.unattributed === 1 ? 'is' : 'are'} not credited to any.`);
  if (t.undated) lines.push(`${plural(t.undated, 'mailshot')} marked as sent with no send date ${t.undated === 1 ? 'is' : 'are'} not shown: a window needs a day to count from.`);
  return lines;
}

export const ABOUT = 'A quote counts for a mailshot when a home it mailed went on to quote, matched by address: most people who respond type the address or come direct, so this is the measure, not visits. Each quote is counted once and credited to the earliest mailshot that reached any record of its home. Each window counts from that mailshot’s own send date, so an old and a new mailshot are compared over the same span: one week here is 7 days, one month 30, three months 91, six months 182. Click a column’s heading to sort by it; a window not yet passed sorts last. “Bought the product” is a quote that went on to buy the Completion Guaranteed product (Purchased or later); a cancelled one is counted apart. A new quote joins its mailshot when the hourly match links it, so today’s may take up to an hour to appear.';

/**
 * Sorting the table by a column's heading (shared rules: utils/tableSort.js). Each accessor gives the figure the
 * column shows; a window not yet passed gives nothing, so it sorts LAST whichever way (never as 0).
 * "Mailshot" sorts by send date; numbers start biggest first, median days smallest first.
 */
export const DEFAULT_SORT = Object.freeze({ key: 'mailed', dir: 'desc' });
export function sortAccessors(windowKeys) {
  const acc = {
    mailed: (r) => r.mailedDay || null,
    homes: (r) => r.homes,
    soFar: (r) => r.perThousand,
    bought: (r) => r.bought,
    cancelled: (r) => r.cancelled,
    median: (r) => r.medianDays,
  };
  for (const k of windowKeys || []) {
    acc[`w:${k}`] = (r) => {
      const w = r.windows && r.windows[k];
      return w && w.passed === true ? w.perThousand : null;
    };
  }
  return acc;
}
export const firstDirOf = (key) => (key === 'median' ? 'asc' : 'desc');
